import React, { useEffect, useRef, useState } from 'react';
import styles from './ItineraryMap.module.css';
import { DAY_COLORS } from '../constants/dayColors';

function toNum(v) {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return n;
}

function isValidCoord(v) {
  const n = toNum(v);
  return typeof n === 'number' && !isNaN(n) && n !== 0;
}

export default function ItineraryMap({ places, days }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const [activeDay, setActiveDay] = useState('all');
  const [leafletReady, setLeafletReady] = useState(false);

  // Load Leaflet from CDN once
  useEffect(() => {
    if (window.L) { setLeafletReady(true); return; }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => setLeafletReady(true);
    document.body.appendChild(script);
  }, []);

  // Initialize map
  useEffect(() => {
    if (!leafletReady || !mapRef.current || mapInstance.current) return;
    const L = window.L;

    const validPlaces = places.filter((p) => isValidCoord(p.lat) && isValidCoord(p.lng));
    const center = validPlaces.length
      ? [toNum(validPlaces[0].lat), toNum(validPlaces[0].lng)]
      : [1.3521, 103.8198]; // fallback: Singapore

    mapInstance.current = L.map(mapRef.current, {
      scrollWheelZoom: false,
    }).setView(center, 12);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(mapInstance.current);

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, [leafletReady]);

  // Render markers whenever places or activeDay changes
  useEffect(() => {
    if (!leafletReady || !mapInstance.current) return;
    const L = window.L;
    const map = mapInstance.current;

    // Clear old markers
    markersRef.current.forEach((m) => map.removeLayer(m));
    markersRef.current = [];

    const filtered = activeDay === 'all'
      ? places.filter((p) => isValidCoord(p.lat) && isValidCoord(p.lng))
      : places.filter((p) => isValidCoord(p.lat) && isValidCoord(p.lng) && String(p.day) === String(activeDay));

    if (filtered.length === 0) return;

    const bounds = [];
    filtered.forEach((place) => {
      const lat = toNum(place.lat);
      const lng = toNum(place.lng);
      const color = DAY_COLORS[((place.day || 1) - 1) % DAY_COLORS.length];
      const icon = L.divIcon({
        className: 'juzgo-marker-wrap',
        html: `<div style="background:${color}" class="juzgo-marker-pin"></div>`,
        iconSize: [22, 22],
        iconAnchor: [11, 22],
      });
      const marker = L.marker([lat, lng], { icon }).addTo(map);
      marker.bindPopup(`<strong>${place.name}</strong><br/>Day ${place.day || '–'} · ${place.type || ''}`);
      markersRef.current.push(marker);
      bounds.push([lat, lng]);
    });

    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    }
  }, [leafletReady, places, activeDay]);

  return (
    <div className={styles.wrap}>
      {/* Day filter tabs */}
      <div className={styles.dayTabs}>
        <button
          className={`${styles.dayTab} ${activeDay === 'all' ? styles.dayTabActive : ''}`}
          onClick={() => setActiveDay('all')}
        >
          All days
        </button>
        {days.map((day, i) => (
          <button
            key={day}
            className={`${styles.dayTab} ${activeDay === day ? styles.dayTabActive : ''}`}
            onClick={() => setActiveDay(day)}
            style={activeDay === day ? { background: DAY_COLORS[(day - 1) % DAY_COLORS.length] } : {}}
          >
            <span
              className={styles.dayDot}
              style={{ background: DAY_COLORS[(day - 1) % DAY_COLORS.length] }}
            />
            Day {day}
          </button>
        ))}
      </div>

      <div ref={mapRef} className={styles.mapBox}>
        {!leafletReady && (
          <div className={styles.mapLoading}>
            <div className={styles.spinner} />
          </div>
        )}
      </div>
    </div>
  );
}
